import React, { useEffect, useState } from "react";
import { AlertTriangle, Sparkles, Loader2, Save, FileText } from "lucide-react";
import { saveMapping, aiMap } from "../../lib/uploadsApi";
import { SampleGrid } from "../uploads/FileUploadSource";
import StandardsCheck from "./StandardsCheck";
import { Pill, btn, num } from "./Layer";
import { cn } from "../../lib/utils";

const slug = (c) => c.replace(/[^a-z0-9]+/gi, "-").toLowerCase();

function Facts({ it }) {
  const p = it.profile || {};
  const facts = [["Rows", num(p.rows)], ["Columns", num(p.columns)], ["Date range", p.date_range ? p.date_range.join(" → ") : "—"],
    ["SKUs", p.skus ? num(p.skus) : "—"], ["Branches", p.branches ? num(p.branches) : "—"]];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-px bg-border border border-border rounded-sm overflow-hidden" data-testid="rv-profile-facts">
      {facts.map(([k, v]) => <div key={k} className="bg-surface px-3 py-2"><div className="kpi-label">{k}</div><div className="font-mono-tabular text-[13px] mt-0.5">{v}</div></div>)}
    </div>
  );
}

// Review step 1: file profile, detected problems, column mapping (with Suggest with AI) and sample rows.
export default function ReviewProfile({ d, meta, canMap, canGuide, onChanged }) {
  const it = d.item;
  const [map, setMap] = useState({});
  const [type, setType] = useState(it.type);
  const [state, setState] = useState({ busy: null, error: null });
  useEffect(() => {
    setType(it.type);
    setMap(Object.fromEntries(Object.entries(it.mapping).map(([c, m]) => [c, m.field || ""])));
  }, [it.mapping, it.type]);
  const run = async (key, fn) => {
    setState({ busy: key, error: null });
    try { await fn(); await onChanged(); setState({ busy: null, error: null }); } catch (e) { setState({ busy: null, error: e.message }); }
  };
  const save = () => run("save", () => saveMapping(it.id, Object.fromEntries(Object.entries(map).map(([c, f]) => [c, f || null])), type, false));
  const free = Object.values(it.mapping).some(m => !m.field && !m.pii);
  return (
    <div className="space-y-5" data-testid="rv-profile">
      <Facts it={it} />
      {it.note && <div className="text-[12px] text-muted inline-flex items-center gap-1.5"><FileText size={12} />Uploader note: {it.note}</div>}
      {it.profile?.problems?.length > 0 && (
        <div className="border border-warning/40 bg-warning/5 rounded-sm p-3 text-[12px] space-y-1" data-testid="rv-problems">
          <div className="kpi-label text-warning mb-1">Detected problems in the raw file</div>
          {it.profile.problems.map(p => <div key={p} className="flex gap-1.5"><AlertTriangle size={12} className="text-warning mt-0.5 shrink-0" />{p}</div>)}
        </div>)}
      <StandardsCheck s={d.standards} canGuide={canGuide} />
      <section className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="kpi-label flex-1">Column mapping · file column → platform field</div>
          <select data-testid="rv-type" disabled={!canMap} value={type} onChange={e => setType(e.target.value)} className="h-8 bg-surface border border-border rounded-sm px-2 text-[12px]">
            <option value="unknown">Unknown type</option>{Object.entries(meta.types).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select>
        </div>
        <div className="divide-y divide-border border border-border rounded-sm">
          {it.columns.map(c => (
            <div key={c} className="flex items-center gap-2 px-3 py-1.5 text-[12.5px]">
              <span className="flex-1 truncate font-mono-tabular">{c}</span>
              {it.mapping[c]?.pii && <Pill tone="warning">Personal</Pill>}
              {it.mapping[c]?.confidence && <span className="text-[10px] text-muted w-14">{it.mapping[c].confidence}</span>}
              <select data-testid={`rv-map-${slug(c)}`} disabled={!canMap} value={map[c] || ""} onChange={e => setMap(m => ({ ...m, [c]: e.target.value }))}
                      className="w-48 h-8 bg-surface border border-border rounded-sm px-1.5 text-[12px]">
                <option value="">— not used —</option>{Object.entries(meta.fields).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
            </div>))}
        </div>
        {state.error && <div className="text-critical text-[12px]" data-testid="rv-map-error">{state.error}</div>}
        {canMap && <div className="flex flex-wrap gap-2">
          {free && <button data-testid="rv-ai-map" disabled={!!state.busy} onClick={() => run("ai", () => aiMap(it.id))} className={cn(btn, "border border-primary/60 text-primary")}>
            {state.busy === "ai" ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}{state.busy === "ai" ? "Thinking…" : "Suggest with AI"}</button>}
          <button data-testid="rv-map-save" disabled={!!state.busy} onClick={save} className={cn(btn, "border border-border hover:border-primary")}>
            {state.busy === "save" ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}Save mapping</button>
        </div>}
      </section>
      <section><div className="kpi-label mb-1.5">Sample rows · raw Bronze copy</div><SampleGrid rows={it.sample} /></section>
    </div>
  );
}
